"use client"

import { useState, useEffect, useMemo, useCallback } from "react"
import { useDispatch } from 'react-redux';
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { pettyCashService } from '../apis/pettycash'
import { employeeManagementService } from '../apis/employeeManagement';
import { toast } from "react-hot-toast"
import moment, { defaultDateRange, startOfDay, endOfDay, isDateTimeInRange, parseAppDateTime } from "../utils/timezone"
import { PettyCashAddModal } from "../components/petty-cash-addmodal"
import { PettyCashEditModal } from "../components/petty-cash-editmodal"
import { PettyCashDeleteModal } from "../components/petty-cash-deletemodal"
import { PettyCashTransactionTable } from "../components/petty-cashtransaction-table"
import DateRangePicker from '../components/ui/date-range-picker'
import { Calendar,Pencil } from "lucide-react"
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from "../context/AuthContext"
import AdminEditModalPettycash from "../components/AdminEditModalPettycash"
import { setPettyCashTransactionDetails } from '../Store/actions/transactionDetails'
import { base64Logo } from '../constants/pdfLogo'

// Helper functions
const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
};

const formatCurrency = (value) => {
    return parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};


export default function PettyCash() {
    const dispatch = useDispatch();
    const { currentUser } = useAuth();
    
    const [user, setUser] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [currentBalance, setCurrentBalance] = useState(0);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [dateRange, setDateRange] = useState(defaultDateRange);
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [currentTransaction, setCurrentTransaction] = useState(null);
    const [mostRecentId, setMostRecentId] = useState(null);
    const [error, setError] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [isAdminEditModalOpen, setIsAdminEditModalOpen] = useState(false);

    // Load saved transaction details from localStorage on component mount
    useEffect(() => {
        const savedDetails = localStorage.getItem('transactionDetailspettycash');
        if (savedDetails) {
            dispatch(setPettyCashTransactionDetails(JSON.parse(savedDetails)));
        }
    }, [dispatch]);

    // Initialize user from localStorage
    useEffect(() => {
        try {
            const userData = localStorage.getItem('user');
            if (userData) {
                setUser(JSON.parse(userData));
            }
        } catch (error) {
            console.error('Error parsing user data:', error);
            setUser(null);
        }
    }, []);

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const response = await employeeManagementService.getAllEmployees({ limit: 1000000 });
             
                if (response.success) {
                    setEmployees(response?.data?.body?.data?.employees);
                }
            } catch (error) {
                console.error('Error fetching employees:', error);
            }
        };

        fetchEmployees();
    }, []);


    // Calculate summary based on date range
    const dateRangeSummary = useMemo(() => {
        const filteredByDateRange = transactions.filter(transaction =>
            isDateTimeInRange(transaction.date, dateRange.start, dateRange.end)
        );

        const deposit = filteredByDateRange.reduce((sum, tx) =>
            tx.transaction_type === "deposit" ? sum + parseNumericValue(tx.transaction_amount) : sum, 0);

        const withdraw = filteredByDateRange.reduce((sum, tx) =>
            tx.transaction_type === "withdraw" ? sum + parseNumericValue(tx.transaction_amount) : sum, 0);

        // Calculate balance based on date range transactions
        const transactionsOutsideRange = transactions.filter(transaction =>
            !isDateTimeInRange(transaction.date, dateRange.start, dateRange.end)
        );

        let balanceAdjustment = 0;
        transactionsOutsideRange.forEach(tx => {
            if (tx.transaction_type === "deposit") {
                balanceAdjustment -= parseNumericValue(tx.transaction_amount);
            } else {
                balanceAdjustment += parseNumericValue(tx.transaction_amount);
            }
        });

        const calculatedBalance = currentBalance + balanceAdjustment;

        return {
            deposit,
            withdraw,
            balance: calculatedBalance
        };
    }, [transactions, dateRange, currentBalance]);

    // Fixed fetchData function - removed unnecessary dependencies
    const fetchData = useCallback(async () => {
        if (!user) return;

        try {
            setLoading(true);

            if (user.role === "Cashier") {
                const verificationRes = await pettyCashService.checkVerificationStatus();
                if (verificationRes.success) {
                    if (verificationRes.data.status === "unverified") {
                        setLoading(false);
                        return;
                    }
                }
            }

            const [balanceRes, transactionsRes] = await Promise.all([
                pettyCashService.getCurrentBalance(),
                pettyCashService.getAllTransactions()
            ]);

            if (balanceRes.success && transactionsRes.success) {
                const balance = parseNumericValue(balanceRes.data.balance);
                const transactionData = transactionsRes.data || [];

                // Sort transactions by date descending
                const sortedTransactions = [...transactionData].sort((a, b) =>
                    (parseAppDateTime(b.date)?.valueOf() ?? 0) - (parseAppDateTime(a.date)?.valueOf() ?? 0)
                );

                setCurrentBalance(balance);
                setTransactions(sortedTransactions);

                // Set most recent transaction ID for deletion validation
                if (sortedTransactions.length > 0) {
                    setMostRecentId(sortedTransactions[0].id);
                }
            } else {
                toast.error("Failed to load data");
            }
        } catch (error) {
            console.error('Fetch error:', error);
            toast.error("Failed to load data");
        } finally {
            setLoading(false);
        }
    }, [user]); // Only depend on user

    // Fixed useEffect - now includes fetchData in dependencies
    useEffect(() => {
        fetchData();
    }, [fetchData, refreshFlag]); // Include both fetchData and refreshFlag

    const openAddModal = () => setIsAddModalOpen(true);
    const openEditModal = (transaction) => {
        setCurrentTransaction(transaction);
        setIsEditModalOpen(true);
    };
    const openDeleteModal = (transaction) => {
        setCurrentTransaction(transaction);
        setIsDeleteModalOpen(true);
    };

    const recalculateBalances = (txList) => {
        const sortedTx = [...txList].sort((a, b) =>
            (parseAppDateTime(a.date)?.valueOf() ?? 0) - (parseAppDateTime(b.date)?.valueOf() ?? 0)
        );

        return sortedTx.map((tx, index) => {
            const amount = parseNumericValue(tx.transaction_amount);

            // For first transaction
            if (index === 0) {
                const beforeBalance = tx.transaction_type === "deposit" ? 0 : amount;
                const afterBalance = tx.transaction_type === "deposit" ? amount : 0;
                return {
                    ...tx,
                    before_balance: beforeBalance,
                    after_balance: afterBalance
                };
            }

            // For subsequent transactions
            const prevTx = sortedTx[index - 1];
            const beforeBalance = parseNumericValue(prevTx.after_balance);
            const afterBalance = tx.transaction_type === "deposit"
                ? beforeBalance + amount
                : beforeBalance - amount;

            return {
                ...tx,
                before_balance: beforeBalance,
                after_balance: afterBalance
            };
        });
    };

    const updateTransactionChain = (updatedTx) => {
        const recalculated = recalculateBalances(updatedTx);
        const sorted = [...recalculated].sort((a, b) =>
            (parseAppDateTime(b.date)?.valueOf() ?? 0) - (parseAppDateTime(a.date)?.valueOf() ?? 0)
        );

        setTransactions(sorted);

        // Update current balance to the latest transaction's after_balance
        if (sorted.length > 0) {
            setCurrentBalance(parseNumericValue(sorted[0].after_balance));
        } else {
            setCurrentBalance(0);
        }

        // Update most recent ID
        setMostRecentId(sorted.length > 0 ? sorted[0].id : null);

    };

    const handleAddTransaction = async (formData) => {
        try {
            setError(null);
            const response = await pettyCashService.addTransaction(formData);

            if (response.success) {
                toast.success(response.message || "Transaction added successfully");
                const newTransaction = response.data;

                // Optimistic update
                const updatedTransactions = [newTransaction, ...transactions];
                updateTransactionChain(updatedTransactions);
                setRefreshFlag(prev => !prev);
                return true;
            } else {
                setError(response.error || "Transaction failed");
                toast.error(response.error || "Transaction failed");
                return false;
            }
        } catch (error) {
            console.error('Submit error:', error);
            setError("Transaction failed");
            toast.error("Transaction failed");
            return false;
        }
    };

    const handleUpdateTransaction = async (id, updatedData) => {
        try {
            const response = await pettyCashService.updateTransaction(id, updatedData);

            if (response.success) {
                toast.success(response.message || "Transaction updated successfully");

                // Optimistic update
                const updatedTransactions = transactions.map(tx =>
                    tx.id === id ? { ...tx, ...response.data } : tx
                );
                updateTransactionChain(updatedTransactions);
                setRefreshFlag(prev => !prev);
                return true;
            } else {
                toast.error(response.error || "Update failed");
                return false;
            }
        } catch (error) {
            console.error('Edit error:', error);
            toast.error("Update failed");
            return false;
        }
    };

    const handleUploadReceipt = async (transaction, receipts) => {
        try {
            const response = await pettyCashService.updateTransaction(transaction.id, {
                date: transaction.date,
                name: transaction.name || '',
                notes: transaction.notes || '',
                department: transaction.department || '',
                receipts,
            });

            if (response.success) {
                const updatedTransactions = transactions.map(tx =>
                    tx.id === transaction.id ? { ...tx, receipts } : tx
                );
                setTransactions(updatedTransactions);
                setRefreshFlag(prev => !prev);
                return true;
            }

            toast.error(response.error || "Failed to save receipt");
            return false;
        } catch (error) {
            console.error('Receipt upload error:', error);
            toast.error("Failed to save receipt");
            return false;
        }
    };

    const handleDeleteTransaction = async (id) => {
        try {
            const response = await pettyCashService.deleteTransaction(id);

            if (response.success) {
                toast.success(response.message || "Transaction deleted successfully");

                // Optimistic update
                const updatedTransactions = transactions.filter(tx => tx.id !== id);
                updateTransactionChain(updatedTransactions);
                setRefreshFlag(prev => !prev);
                return true;
            } else {
                toast.error(response.error || "Deletion failed");
                return false;
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error("Deletion failed");
            return false;
        }
    };


    const handleDateRangeApply = (startDate, endDate) => {
        setDateRange({
            start: startOfDay(startDate),
            end: endOfDay(endDate)
        });
        setIsDatePickerOpen(false);
        setCurrentPage(1);
    };

    const formatDateRange = () => {
        return `${moment(dateRange.start).format("MM/DD/YYYY")} - ${moment(dateRange.end).format("MM/DD/YYYY")}`;
    };

    // Filter transactions based on search term and date range
    const filteredTransactions = transactions
        .filter(transaction => {
            const matchesSearch = !searchTerm ||
                (transaction.name && transaction.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (transaction.notes && transaction.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (transaction.added_by && transaction.added_by.toLowerCase().includes(searchTerm.toLowerCase()));

            const matchesDateRange = isDateTimeInRange(
                transaction.date,
                dateRange.start,
                dateRange.end
            );

            return matchesSearch && matchesDateRange;
        })
        .map(transaction => ({
            ...transaction,
            isDeletable: transaction.id === mostRecentId
        }));

    if (!user) {
        return (
            <div className="flex justify-center items-center py-8">
                <div className="p-6 bg-gray-50">
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
                    </div>
                </div>
            </div>
        );
    }


    const exportPettyCashToPDF = () => {
        try {
            const doc = new jsPDF();

            // Set document properties


            const logoWidth = 40; // adjust width as needed
            const logoHeight = 20; // adjust height as needed
            doc.addImage(base64Logo, 'PNG', 150, 10, logoWidth, logoHeight); // x, y, width, height


            doc.setProperties({
                title: 'Petty Cash Report',
                subject: 'Petty Cash Transactions',
                author: 'Elite Poker Lounge',
                creator: 'Petty Cash System'
            });

            // Add timestamp
            const now = new Date();
            const dateTime = now.toLocaleString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });

            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Date & Time: ${dateTime}`, 14, 15);
            doc.text(`Date Range: ${formatDateRange()}`, 14, 25);

            // Title
            doc.setFontSize(18);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text('Petty Cash Transactions', 14, 40);

            // Summary section
            doc.setFontSize(12);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text('Summary:', 14, 50);

            doc.setFont(undefined, 'normal');
            doc.text(`Total Deposit: $${formatCurrency(dateRangeSummary.deposit)}`, 14, 60);
            doc.text(`Total Withdraw: $${formatCurrency(dateRangeSummary.withdraw)}`, 14, 70);
            doc.text(`Balance: $${formatCurrency(dateRangeSummary.balance)}`, 14, 80);

            // Department-wise totals (group by department)
            const departmentTotals = {};
            filteredTransactions.forEach((tx) => {
                const key = (tx.department || '').trim() || 'Unspecified';
                if (!departmentTotals[key]) {
                    departmentTotals[key] = { withdraw: 0, deposit: 0 };
                }
                const amount = parseNumericValue(tx.transaction_amount);
                if (tx.transaction_type === 'deposit') {
                    departmentTotals[key].deposit += amount;
                } else if (tx.transaction_type === 'withdraw') {
                    departmentTotals[key].withdraw += amount;
                }
            });

            const departmentEntries = Object.entries(departmentTotals).sort((a, b) =>
                a[0].localeCompare(b[0])
            );

            let yPos = 92;
            doc.setFont(undefined, 'bold');
            doc.setFontSize(12);
            doc.text('Department Wise:', 14, yPos);
            yPos += 8;

            doc.setFont(undefined, 'normal');
            doc.setFontSize(10);

            if (departmentEntries.length === 0) {
                doc.text('No department transactions in this range.', 14, yPos);
                yPos += 8;
            } else {
                departmentEntries.forEach(([departmentName, totals], index) => {
                    const parts = [`${index + 1}. ${departmentName}:`];
                    if (totals.withdraw > 0) {
                        parts.push(`Total Withdraw = $${formatCurrency(totals.withdraw)}`);
                    }
                    if (totals.deposit > 0) {
                        parts.push(`Total Deposit = $${formatCurrency(totals.deposit)}`);
                    }
                    if (totals.withdraw === 0 && totals.deposit === 0) {
                        parts.push('Total Withdraw = $0.00 | Total Deposit = $0.00');
                    }
                    const line = parts.length > 2
                        ? `${parts[0]} ${parts.slice(1).join(' | ')}`
                        : `${parts[0]} ${parts[1]}`;
                    const wrapped = doc.splitTextToSize(line, 180);

                    if (yPos + wrapped.length * 5 > 280) {
                        doc.addPage();
                        yPos = 20;
                    }

                    doc.text(wrapped, 14, yPos);
                    yPos += wrapped.length * 5 + 2;
                });
            }

            yPos += 6;

            // Prepare table data
            const tableHeaders = [
                'Date',
                'Petty Cash Balance',
                'Transaction',
                'Department',
                'Transaction Details',
                'Cash Given To',
                'After Balance',
                'Added By'
            ];

            const tableData = filteredTransactions.map(transaction => {
                const transactionAmount = transaction.transaction_type === 'deposit'
                    ? `+${formatCurrency(transaction.transaction_amount)}`
                    : `-${formatCurrency(transaction.transaction_amount)}`;

                return [
                    moment(transaction.date).format("MM/DD/YYYY hh:mm A"),
                    `$${formatCurrency(transaction.before_balance)}`,
                    transactionAmount,
                    transaction.department || 'N/A',
                    transaction.notes || 'N/A',
                    transaction.name || 'N/A',
                    `$${formatCurrency(transaction.after_balance)}`,
                    transaction.added_by || 'N/A'
                ];
            });

            // Add table
            autoTable(doc, {
                head: [tableHeaders],
                body: tableData,
                startY: yPos,
                theme: 'plain',
                styles: {
                    fontSize: 8,
                    cellPadding: 2,
                    textColor: [0, 0, 0],
                    lineColor: [0, 0, 0],
                    lineWidth: 0.1
                },
                headStyles: {
                    fillColor: false,
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                    lineWidth: 0.1,
                    lineColor: [0, 0, 0]
                },
                columnStyles: {
                    0: { cellWidth: 22 },
                    1: { cellWidth: 22 },
                    2: { cellWidth: 18 },
                    3: { cellWidth: 22 },
                    4: { cellWidth: 32 },
                    5: { cellWidth: 22 },
                    6: { cellWidth: 22 },
                    7: { cellWidth: 22 }
                },
                margin: { left: 14, right: 14 },
                tableLineColor: [0, 0, 0],
                tableLineWidth: 0.1
            });

            // Save the PDF
            const fileName = `Petty_Cash_Report_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}.pdf`;
            doc.save(fileName);
            toast.success('PDF exported successfully!');
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error('Error generating PDF. Please try again.');
        }
    };

    const handleAdminEditSave = (updatedDetails) => {
        dispatch(setPettyCashTransactionDetails(updatedDetails));
        localStorage.setItem('transactionDetailspettycash', JSON.stringify(updatedDetails));
    };


    // Update the Admin/edit button click handler
    const handleAdminEditClick = () => {
        setIsAdminEditModalOpen(true);
    };

    return (
        
        <div className="bg-gray-50 p-6">
                <div className="flex justify-between items-center w-full mb-8">
                    <div className="text-center flex-1">
                        <h1 className="text-3xl font-bold text-gray-800 tracking-tight">
                            Petty Cash Transactions
                        </h1>
                        <p className="text-gray-600 mt-2 text-sm">
                            Manage and track petty cash transactions
                        </p>
                    </div>
                    {/* Hidden for now — re-enable by removing `false &&` when Admin edit is needed again */}
                    {false && currentUser?.role?.trim() === "Admin" && (
                        <div
                            className="flex items-center gap-2 text-sm text-gray-700 hover:text-blue-600 px-3 py-1.5 rounded-md border border-gray-300 hover:border-blue-500 shadow-sm transition-colors duration-200 cursor-pointer"
                            onClick={handleAdminEditClick}
                        >
                            <Pencil className="w-4 h-4" />
                            <span className="font-medium">Admin</span>
                        </div>
                    )}


                </div>

                {/* Summary row: date range first, then metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
                    <Card className="bg-white shadow-sm border border-gray-200">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                                Date Range
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                            <div className="flex justify-center">
                                <Button
                                    variant="outline"
                                    onClick={() => setIsDatePickerOpen(true)}
                                    className="w-full justify-start text-left font-normal"
                                >
                                    <Calendar className="mr-2 h-4 w-4 shrink-0" />
                                    <span className="truncate">{formatDateRange()}</span>
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-white shadow-sm border border-gray-200">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                                Total Deposit
                                <span className="block text-xs text-gray-400 normal-case mt-1">
                                    ({formatDateRange()})
                                </span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-green-600">
                                    ${formatCurrency(dateRangeSummary.deposit)}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-white shadow-sm border border-gray-200">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                                Total Money Withdraw
                                <span className="block text-xs text-gray-400 normal-case mt-1">
                                    ({formatDateRange()})
                                </span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-red-600">
                                    ${formatCurrency(dateRangeSummary.withdraw)}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-white shadow-sm border border-gray-200">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                                Current Balance
                                <span className="block text-xs text-gray-400 normal-case mt-1">
                                    (Live Balance)
                                </span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600">
                                    ${formatCurrency(currentBalance)}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Transaction Form */}
                <Card className="bg-white shadow-sm border border-gray-200 mb-8">
                    <CardHeader>
                        <CardTitle className="text-lg font-semibold text-gray-800">
                            Add New Transaction
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex justify-center">
                            <Button
                                onClick={openAddModal}
                                className="h-12 px-8 bg-yellow-400 hover:bg-yellow-500 text-black font-medium"
                            >
                                Add New Transaction
                            </Button>
                        </div>
                        {error && (
                            <div className="mt-4 text-center text-red-600 font-medium">
                                {error}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Transaction History */}
                <Card className="bg-white shadow-sm border border-gray-200">
                    <CardHeader>
                        <div className="flex justify-between items-center">
                            <CardTitle className="text-lg font-semibold text-gray-800">
                                Transaction History
                            </CardTitle>
                            <Button
                                onClick={exportPettyCashToPDF}
                                variant="outline"
                                className="bg-yellow-400 hover:bg-yellow-500 text-black"
                            >
                                Export to PDF
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {/* Controls */}
                        <div className="flex flex-col sm:flex-row justify-end items-start sm:items-center gap-3 mb-6">
                            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                                Search:
                            </label>
                            <Input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search transactions..."
                                className="w-64"
                            />
                        </div>

                        {/* Loading State */}
                        {loading ? (
                            <div className="flex justify-center items-center py-8">
                                <div className="p-6 bg-gray-50">
                                    <div className="flex justify-center items-center h-64">
                                        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <PettyCashTransactionTable
                                transactions={filteredTransactions}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                entriesPerPage={entriesPerPage}
                                setEntriesPerPage={setEntriesPerPage}
                                onEdit={openEditModal}
                                onDelete={openDeleteModal}
                                onUploadReceipt={handleUploadReceipt}
                                dateRange={dateRange}
                                employeeApiService={employeeManagementService}
                                employees={employees}
                            />
                        )}
                    </CardContent>
                </Card>



                {/* Add Modal */}
                <PettyCashAddModal
                    isOpen={isAddModalOpen}
                    onClose={() => setIsAddModalOpen(false)}
                    onAddTransaction={handleAddTransaction}
                    currentBalance={currentBalance}
                />


                <AdminEditModalPettycash
                    isOpen={isAdminEditModalOpen}
                    onClose={() => setIsAdminEditModalOpen(false)}
                    onSave={handleAdminEditSave}
                />
                {/* Edit Modal */}
                <PettyCashEditModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    transaction={currentTransaction}
                    onUpdateTransaction={handleUpdateTransaction}
                    currentUser={currentUser || user}
                />

                {/* Delete Modal */}
                <PettyCashDeleteModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    transaction={currentTransaction}
                    onDeleteTransaction={handleDeleteTransaction}
                />
            {isDatePickerOpen && (
                <DateRangePicker
                    isOpen={isDatePickerOpen}
                    onClose={() => setIsDatePickerOpen(false)}
                    onApply={handleDateRangeApply}
                    initialStartDate={dateRange.start}
                    initialEndDate={dateRange.end}
                />
            )}
        </div>
    );
}